import { useMemo } from 'react';
import { Box, List, ListItemButton, ListItemText, Paper, Tab, Tabs, Typography, Chip } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SessionEvidence } from '@k-ucap/shared';
import { SessionEvidenceType } from '@k-ucap/shared';
import { buildDeepLink } from '@/hooks/useDeepLinkContext.js';

interface EvidencePanelProps {
  evidence: SessionEvidence[];
  errors: string[];
  sessionId: string | null;
}

const evidenceLink = (item: SessionEvidence, sessionId: string | null): string => {
  const base = {
    sessionId,
    runId: item.runId ?? null,
    messageId: item.messageId ?? item.messageEventId ?? null,
    timestamp: item.timestamp,
    evidenceId: item.id,
  };
  switch (item.type) {
    case SessionEvidenceType.GIS_POSITION:
    case SessionEvidenceType.GIS:
      return buildDeepLink('/gis', base);
    case SessionEvidenceType.TIMING_ERROR:
    case SessionEvidenceType.REPLAY:
      return buildDeepLink('/scenario', base);
    case SessionEvidenceType.SYSTEM_ERROR:
    case SessionEvidenceType.ERROR:
      return '/settings?tab=diagnostics';
    default:
      return buildDeepLink('/analyzer', base);
  }
};

export const EvidencePanel = ({ evidence, errors, sessionId }: EvidencePanelProps) => {
  const [tab, setTab] = useState(0);
  const navigate = useNavigate();

  const sortedEvidence = useMemo(
    () => [...evidence].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 100),
    [evidence]
  );

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable">
        <Tab label={`Evidence (${sortedEvidence.length})`} />
        <Tab label={`Errors (${errors.length})`} />
        <Tab label="Console" />
      </Tabs>
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
        {tab === 0 &&
          (sortedEvidence.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              기록된 Evidence가 없습니다. FAIL/WARN 이벤트가 발생하면 자동으로 기록됩니다.
            </Typography>
          ) : (
            <List dense data-testid="evidence-list">
              {sortedEvidence.map((item) => (
                <ListItemButton key={item.id} onClick={() => navigate(evidenceLink(item, sessionId))}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          label={item.severity ?? 'INFO'}
                          size="small"
                          color={item.severity === 'FAIL' ? 'error' : item.severity === 'WARN' ? 'warning' : 'default'}
                        />
                        <Typography variant="body2">{item.title ?? item.type}</Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        {item.description && <Typography variant="caption">{item.description}</Typography>}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          ))}
        {tab === 1 &&
          (errors.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              오류가 없습니다.
            </Typography>
          ) : (
            <List dense>
              {errors.map((error, idx) => (
                <Typography key={idx} variant="body2" color="error" sx={{ p: 0.5 }}>
                  {error}
                </Typography>
              ))}
            </List>
          ))}
        {tab === 2 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            실행 콘솔 출력이 여기에 표시됩니다.
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
